// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useEffect, useRef, useState } from "react"
import { EditModeHost } from "./edit-mode-host"
import { ReviewWorklistBody } from "../review/review-worklist"
import { MobilePendingBar } from "./mobile-pending-bar"
import { getReviewState } from "../review/review-store"
import type { CartItem } from "./edit-cart"

type Tab = "edit" | "review"
type Snap = "peek" | "half" | "full"

const COCKPIT_TAB_KEY = "ahead-cockpit-tab"

// Snap cycle order for tapping the handle; pointer-drag snaps to the nearest of
// these by their target height fraction of the viewport.
const SNAP_ORDER: Snap[] = ["peek", "half", "full"]
const SNAP_FRACTION: Record<Snap, number> = {
  peek: 0.13,
  half: 0.55,
  full: 0.92,
}

/**
 * Mirrors initialTab() from the desktop Cockpit: land on Review when the URL asks
 * for it (`?review-cockpit` or the `?review=<key>` a jump carries), else restore
 * the last tab from session storage so stepping the worklist never drops to Edit.
 */
function initialTab(): Tab {
  if (typeof window === "undefined") return "edit"
  const params = new URLSearchParams(window.location.search)
  if (params.has("review-cockpit") || params.has("review")) return "review"
  return sessionStorage.getItem(COCKPIT_TAB_KEY) === "review"
    ? "review"
    : "edit"
}

/** Snap point whose viewport fraction is closest to `fraction`. */
function nearestSnap(fraction: number): Snap {
  return SNAP_ORDER.reduce((best, s) =>
    Math.abs(SNAP_FRACTION[s] - fraction) <
    Math.abs(SNAP_FRACTION[best] - fraction)
      ? s
      : best
  )
}

/**
 * Mobile bottom-sheet shell. The mobile counterpart to the desktop Cockpit's
 * right rail — same Edit/Review tabs and the same EditModeHost + pending-cart +
 * ReviewWorklistBody children, but presented as a draggable, snap-pointed sheet
 * over the (still-visible, still-scrollable) page. It NEVER adds
 * `ahead-cockpit-docked` to <html>, so the page never reflows on mobile.
 */
export function MobileCockpit({
  active,
  identity,
  onExit,
}: {
  active: boolean
  identity: { idToken: string; environment: string; branch?: string }
  onExit: () => void
}) {
  const [tab, setTab] = useState<Tab>(initialTab)
  const [snap, setSnap] = useState<Snap>("half")
  const [preview, setPreview] = useState(true)
  const [reopen, setReopen] = useState<((item: CartItem) => void) | null>(null)
  // The Edit tab's body is just a host slot: EditModeHost portals either the
  // "tap any text" hint or the in-context string editor into it.
  const [editSlot, setEditSlot] = useState<HTMLDivElement | null>(null)
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const editTabRef = useRef<HTMLButtonElement | null>(null)
  const reviewTabRef = useRef<HTMLButtonElement | null>(null)
  // Vertical drag tracking for the handle (commit the snap on pointerup).
  const dragStartY = useRef<number | null>(null)
  const dragFraction = useRef<number | null>(null)

  // Remember the active tab so a cross-locale jump that remounts the sheet comes
  // back on Review instead of resetting to Edit (mirrors the desktop Cockpit).
  useEffect(() => {
    sessionStorage.setItem(COCKPIT_TAB_KEY, tab)
  }, [tab])

  // Keyboard avoidance: lift the sheet above the on-screen keyboard. The
  // visualViewport shrinks when the keyboard opens; the difference between the
  // layout viewport and the visual viewport is the keyboard inset.
  useEffect(() => {
    if (!active) return
    const vv = window.visualViewport
    if (!vv) return
    const sync = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      sheetRef.current?.style.setProperty("--ahead-kb-inset", `${inset}px`)
    }
    sync()
    vv.addEventListener("resize", sync)
    // iOS fires `scroll` (not always `resize`) on the visual viewport as the
    // keyboard animates in/out and as it shifts offsetTop — listen to both so the
    // inset tracks the keyboard instead of leaving the sheet stranded behind it. (AHD-7513)
    vv.addEventListener("scroll", sync)
    return () => {
      vv.removeEventListener("resize", sync)
      vv.removeEventListener("scroll", sync)
    }
  }, [active])

  // Escape collapses the sheet to peek (standard dismiss affordance), but never
  // while typing in a field — there Escape should blur the input, not the sheet.
  useEffect(() => {
    if (!active) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return
      const el = e.target as HTMLElement | null
      const tag = el?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return
      setSnap("peek")
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [active])

  // Focus management: when the tab switches (and the sheet is open), move focus
  // into the new content so a keyboard / screen-reader user lands in the panel
  // rather than being stranded on the tab. The body has tabIndex={-1} as the
  // programmatic landing spot.
  useEffect(() => {
    if (!active || snap === "peek") return
    bodyRef.current?.focus()
  }, [tab, active, snap])

  if (!active) return null

  // Cycle to the next snap point (handle tap / Enter / Space share this).
  function cycleSnap() {
    setSnap((s) => SNAP_ORDER[(SNAP_ORDER.indexOf(s) + 1) % SNAP_ORDER.length])
  }

  // Handle pointer: a tap cycles peek→half→full→peek; a vertical drag snaps to
  // the nearest point, committed on pointerup.
  function onHandlePointerDown(e: React.PointerEvent) {
    dragStartY.current = e.clientY
    dragFraction.current = null
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  function onHandlePointerMove(e: React.PointerEvent) {
    if (dragStartY.current === null) return
    // Sheet grows as the handle moves up, so fraction = (viewport bottom gap).
    const fraction = (window.innerHeight - e.clientY) / window.innerHeight
    dragFraction.current = fraction
    setSnap(nearestSnap(fraction))
  }
  function onHandlePointerUp(e: React.PointerEvent) {
    const moved =
      dragStartY.current !== null &&
      Math.abs(e.clientY - dragStartY.current) > 6
    if (!moved) {
      // Tap: cycle to the next snap point.
      cycleSnap()
    } else {
      // Drag: snap to the point nearest where the finger ended. Fall back to the
      // final pointer position when no pointermove fired (a fast flick), so a
      // quick slide still commits instead of doing nothing. (AHD-7513)
      const fraction =
        dragFraction.current ??
        (window.innerHeight - e.clientY) / window.innerHeight
      setSnap(nearestSnap(fraction))
    }
    dragStartY.current = null
    dragFraction.current = null
  }

  // Collapsed pill content: current mode + (review) progress, read live.
  const review = getReviewState()
  const reviewProgress =
    tab === "review" && review.current
      ? ` ${review.cursor + 1} / ${review.queue.length}`
      : ""

  return (
    <div
      ref={sheetRef}
      className="ahead-mobile-cockpit"
      data-snap={snap}
      data-ahead-editor-ui
    >
      <div
        className="ahead-mobile-handle"
        role="button"
        tabIndex={0}
        aria-label={`Resize panel, currently ${snap}`}
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            cycleSnap()
          }
        }}
      />

      {snap === "peek" ? (
        <button
          type="button"
          className="ahead-mobile-pill"
          onClick={() => setSnap("half")}
        >
          {tab === "review" ? "Review" : "Edit"}
          {reviewProgress}
        </button>
      ) : (
        <>
          <div
            className="ahead-mobile-tabs"
            role="tablist"
            aria-label="Editor mode"
            onKeyDown={(e) => {
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
              e.preventDefault()
              const next: Tab = tab === "edit" ? "review" : "edit"
              setTab(next)
              ;(next === "edit" ? editTabRef : reviewTabRef).current?.focus()
            }}
          >
            <button
              ref={editTabRef}
              id="ahead-mobile-tab-edit"
              role="tab"
              aria-selected={tab === "edit"}
              aria-controls="ahead-mobile-panel"
              tabIndex={tab === "edit" ? 0 : -1}
              className="ahead-mobile-tab"
              onClick={() => setTab("edit")}
            >
              Edit
            </button>
            <button
              ref={reviewTabRef}
              id="ahead-mobile-tab-review"
              role="tab"
              aria-selected={tab === "review"}
              aria-controls="ahead-mobile-panel"
              tabIndex={tab === "review" ? 0 : -1}
              className="ahead-mobile-tab"
              onClick={() => setTab("review")}
            >
              Review
            </button>
            <button
              type="button"
              className="ahead-cockpit-exit ahead-mobile-exit"
              onClick={onExit}
              title="Exit edit mode"
            >
              ✕ Done
            </button>
          </div>

          <div
            ref={bodyRef}
            id="ahead-mobile-panel"
            className="ahead-mobile-body"
            role="tabpanel"
            tabIndex={-1}
            aria-labelledby={
              tab === "edit"
                ? "ahead-mobile-tab-edit"
                : "ahead-mobile-tab-review"
            }
          >
            {tab === "edit" ? (
              <>
                <div className="ahead-cockpit-edit-slot" ref={setEditSlot} />
                <MobilePendingBar
                  identity={{ idToken: identity.idToken }}
                  currentRef={identity.branch || "preflight"}
                  env={identity.environment}
                  preview={preview}
                  onTogglePreview={setPreview}
                  onEditItem={reopen ?? undefined}
                />
              </>
            ) : (
              <ReviewWorklistBody
                idToken={identity.idToken}
                scopeOpen={false}
              />
            )}
          </div>
        </>
      )}

      {/* Edit overlays/tap-capture must stay mounted whenever the Edit tab is
          active (same contract as the desktop Cockpit), independent of snap. */}
      {tab === "edit" ? (
        <EditModeHost
          active={active && tab === "edit"}
          identity={identity}
          preview={preview}
          onTogglePreview={setPreview}
          onRegisterReopen={(fn) => setReopen(() => fn)}
          onEditPanelActiveChange={(open) => {
            // A tapped string's editor lives in the body, which is hidden at
            // peek — rise to half so the editor is actually visible.
            if (open) setSnap((s) => (s === "peek" ? "half" : s))
          }}
          editSlot={editSlot}
        />
      ) : null}
    </div>
  )
}
